import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { ChevronDown, ChevronUp,  Shield, ShieldOff,  MoreHorizontal, Pen, Eye, UserCheck, Search, User as UserIcon, X, Check, Loader2, Briefcase, Pencil } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,DialogDescription } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,  } from '../ui/alert-dialog';
import { User } from '../types/user';
import { cn } from '../lib/utils';
import { motion, useAnimation } from "framer-motion";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { changeUserPassword,  changeUserRole, updateUserDetails, updateUserContactDetails, updateUserCanCreateUsers, updateUserCanPostJobs } from '../../services/usersapi';
import { toast } from 'react-hot-toast';
import { useRbac } from '../../context';
import { UserPlus } from 'lucide-react';
import { getAssignableJobs, assignJob, assignJobTpoApi, getJobDetailsById, getJobsAssignedToUser } from '../../services/jobsapi';

type UserWithAssignments = User & {
  assignedJobs?: Array<{
    jobId: string | { toString(): string };
    assignedBy: string | { toString(): string };
    assignedAt: string;
  }>;
};

interface UserTableProps {
  users: UserWithAssignments[];
  setUsers: (users: UserWithAssignments[]) => void;
  isloading: boolean;
  onUserSelect: (user: User) => void;
  expandedRows: string[];
  setExpandedRows: (rows: string[]) => void;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (column: string) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onUserReassign: (user: User) => void;
  onBlockToggle: (user: User) => void;
  onSourceChange: (user: User) => void;
  blockLoading: string | null;
  isListingTPO?: boolean;
}



const UserTable: React.FC<UserTableProps> = ({
  users,
  setUsers,
  isloading,
  onUserSelect,
  expandedRows,
  setExpandedRows,
  sortBy,
  sortOrder,
  onSort,
  currentPage,
  totalPages,
  onPageChange,
  onUserReassign,
  onBlockToggle,
  blockLoading,
  isListingTPO=false
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedManagerUserId, setSelectedManagerUserId] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [actionUser, setActionUser] = useState<UserWithAssignments | null>(null);
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [blockConfirmUser, setBlockConfirmUser] = useState<UserWithAssignments | null>(null);
  const [phoneUpdateLoading, setPhoneUpdateLoading] = useState(false);
  const [assignmentInProgress, setAssignmentInProgress] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editUser, setEditUser] = useState<UserWithAssignments | null>(null);
  const [editUserForm, setEditUserForm] = useState({ username: '', email: '', phone: '' });
  const [editUserErrors, setEditUserErrors] = useState({ username: '', email: '', phone: '' });
  const [editUserSaving, setEditUserSaving] = useState(false);
  const [canCreateUsersLoading, setCanCreateUsersLoading] = useState<string | null>(null);
  const [canPostJobsLoading, setCanPostJobsLoading] = useState<string | null>(null);

  // for listing TPO
  const [selectedUserId, setSelectedUserId] = useState<string | null>("null")
  const [dialogOpen, setDialogOpen] = useState(false)
  // Job assignment dialog state - changed to show jobs instead of users
  const [isLoadingJobs, setIsLoadingJobs] = useState(false)
  const JOBS_PAGE_SIZE = 15
  const [assignableJobs, setAssignableJobs] = useState<any[]>([])
  const [selectedJobs, setSelectedJobs] = useState<string[]>([])
  const [initialSelectedJobs, setInitialSelectedJobs] = useState<string[]>([]) // Track initial state
  const [selectedJobsMap, setSelectedJobsMap] = useState<Map<string, { title: string; companyName: string; location: string }>>(new Map()) // Store selected job details
  const [jobSearchTerm, setJobSearchTerm] = useState('')
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Assigned jobs list dialog state
  const [assignedJobsDialogOpen, setAssignedJobsDialogOpen] = useState(false)
  const [assignedJobsUserId, setAssignedJobsUserId] = useState<string | null>(null)
  const [assignedJobs, setAssignedJobs] = useState<any[]>([])
  const [isLoadingAssignedJobs, setIsLoadingAssignedJobs] = useState(false)



  const controls = useAnimation();
  const DURATION = 1;
  const rbacUser = useRbac();

  const [localUsers, setLocalUsers] = useState<UserWithAssignments[]>(users);

  // Sync localUsers state with users prop
  useEffect(() => {
    setLocalUsers(users);
  }, [users]);

  // Log the users array to check roleHistory data
  useEffect(() => {
    console.log('Users array:', users);
  }, [users]);

  // Start animation when the role history dialog opens
  useEffect(() => {
    if (isRoleDialogOpen && actionUser?.roleHistory) {
      console.log(`Opening role history for user: ${actionUser.username}`, actionUser.roleHistory);
      setProgress(0);
      // Animate the progress state manually instead of using Framer Motion's controls.start with an invalid property
      setProgress(0);
      const target = actionUser?.roleHistory?.length ?? 0;
      let start = 0;
      const step = () => {
        start += 1;
        setProgress(start);
        if (start < target) {
          setTimeout(step, (DURATION * 1000) / target);
        }
      };
      if (target > 0) {
        step();
      }
    } else {
      setProgress(0);
    }
  }, [isRoleDialogOpen, actionUser, controls]);

  useEffect(() => {
    controls.start("visible");
  }, []);
  useEffect(() => {
    console.log("actionUser changed:", actionUser);
  }, [actionUser]);

  const formatRelativeTime = (dateString?: string) => {
    if (!dateString) return 'No activity';
    const target = new Date(dateString);
    if (isNaN(target.getTime())) return 'No activity';

    const now = new Date();
    const diffMs = now.getTime() - target.getTime();
    if (diffMs < 0) return 'Just now';

    const minutes = Math.floor(diffMs / (1000 * 60));
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} min${minutes === 1 ? '' : 's'} before`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} before`;

    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} day${days === 1 ? '' : 's'} before`;

    const weeks = Math.floor(days / 7);
    if (weeks < 5) return `${weeks} week${weeks === 1 ? '' : 's'} before`;

    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months === 1 ? '' : 's'} before`;

    const years = Math.floor(days / 365);
    return `${years} year${years === 1 ? '' : 's'} before`;
  };

  // const containerVariants = {
  //   hidden: { opacity: 0 },
  //   visible: {
  //     opacity: 1,
  //     transition: {
  //       staggerChildren: 0.3,
  //       delayChildren: 0.4,
  //     },
  //   },
  // };


  

  // const toggleExpandRow = (userId: string) => {
  //   if (expandedRows.includes(userId)) {
  //     setExpandedRows(expandedRows.filter(id => id !== userId));
  //   } else {
  //     setExpandedRows([...expandedRows, userId]);
  //   }
  // };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'HR': return 'bg-blue-100 text-blue-800 border-blue-200 hover:border-blue-800 hover:bg-white';
      case 'SHM':
      case 'SHM_ADMIN': return 'bg-purple-100 text-purple-800 border-purple-200 hover:border-purple-800 hover:bg-white';
      case 'AGENCY_ADMIN': return 'bg-teal-100 text-teal-800 border-teal-200 hover:border-teal-800 hover:bg-white';
      case 'FBDE_ADMIN': return 'bg-amber-100 text-amber-800 border-amber-200 hover:border-amber-800 hover:bg-white';
      case 'BDE': return 'bg-emerald-100 text-emerald-800 border-emerald-200 hover:border-emerald-800 hover:bg-white';
      default: return 'bg-gray-100 text-gray-800 border-gray-200 hover:border-gray-800 hover:bg-white';
    }
  };


  // Job selection functions
  const getJobId = (job: any): string => {
    const id = job?.id ?? job?._id ?? ''
    return id !== null && id !== undefined ? String(id) : ''
  }

  // Helper function to normalize user IDs for comparison
  const normalizeUserId = (userId: any): string => {
    if (!userId) return ''
    
    let normalized = ''
    try {
      if (typeof userId === 'string') {
        normalized = userId.trim()
      } else if (userId && typeof userId.toString === 'function') {
        normalized = userId.toString().trim()
      } else if (userId && userId._id) {
        const id = userId._id
        normalized = typeof id === 'string' ? id.trim() : String(id).trim()
      } else {
        normalized = String(userId).trim()
      }
      // Remove any ObjectId wrapper if present
      normalized = normalized.replace(/^ObjectId\(['"]?/, '').replace(/['"]?\)$/, '')
    } catch (e) {
      console.warn('Error normalizing userId:', userId, e)
      return ''
    }
    return normalized
  }

  const handleJobToggle = (jobId: string) => {
    const normalizedId = jobId != null ? String(jobId) : ''
    if (
      normalizedId === '' ||
      normalizedId === 'undefined' ||
      normalizedId === 'null'
    ) {
      return
    }
    setSelectedJobs(prev => {
      const isCurrentlySelected = prev.includes(normalizedId)
      if (isCurrentlySelected) {
        // Remove from selection
        setSelectedJobsMap(prevMap => {
          const newMap = new Map(prevMap)
          newMap.delete(normalizedId)
          return newMap
        })
        return prev.filter(id => id !== normalizedId)
      } else {
        // Add to selection - also store job details
        const job = assignableJobs.find(j => getJobId(j) === normalizedId)
        if (job) {
          setSelectedJobsMap(prevMap => {
            const newMap = new Map(prevMap)
            newMap.set(normalizedId, {
              title: job.title || 'Untitled Job',
              companyName: job.companyName || job.company?.name || 'Company',
              location: job.location || 'Location not specified'
            })
            return newMap
          })
        }
        return [...prev, normalizedId]
      }
    })
  }

  const handleRemoveJob = (jobId: string) => {
    setSelectedJobs(prev => prev.filter(id => id !== jobId))
    setSelectedJobsMap(prevMap => {
      const newMap = new Map(prevMap)
      newMap.delete(jobId)
      return newMap
    })
  }

  const getSelectedJobTitles = (): Array<{ id: string; title: string; companyName: string; location: string }> => {
    return selectedJobs
      .map(jobId => {
        // First try to get from the map (for jobs that were selected but might not be in current assignableJobs)
        const jobFromMap = selectedJobsMap.get(jobId)
        if (jobFromMap) {
          return { id: jobId, ...jobFromMap }
        }
        // Fallback to assignableJobs if available
        const job = assignableJobs.find(j => getJobId(j) === jobId)
        if (job) {
          return {
            id: jobId,
            title: job.title || 'Untitled Job',
            companyName: job.companyName || job.company?.name || 'Company',
            location: job.location || 'Location not specified'
          }
        }
        return null
      })
      .filter((job): job is { id: string; title: string; companyName: string; location: string } => job !== null)
  }

  // Use all assignable jobs for display (don't filter out selected ones)
  const filteredJobIds = useMemo(() => {
    return assignableJobs
      .map(job => getJobId(job))
      .filter((id): id is string => Boolean(id))
  }, [assignableJobs])

  const areAllFilteredSelected = filteredJobIds.length > 0 && filteredJobIds.every(id => selectedJobs.includes(id))

  const handleToggleSelectAllFiltered = () => {
    setSelectedJobs(prev => {
      if (areAllFilteredSelected) {
        return prev.filter(id => !filteredJobIds.includes(id))
      }
      const combined = new Set([...prev, ...filteredJobIds])
      return Array.from(combined)
    })
  }

  const fetchAssignableJobsForUser = useCallback(
    async (userId: string, options: { search?: string } = {}) => {
      setIsLoadingJobs(true)
      try {
        const searchValue = options.search?.trim()
        const response = await getAssignableJobs(userId, searchValue ? { search: searchValue } : { limit: JOBS_PAGE_SIZE })
        console.log("Assignable Jobs fetched successfully:", response.data)
        const jobs = response.data?.jobs || response.data || []
        // Keep all jobs in assignableJobs - all jobs are shown in the list with checkboxes
        setAssignableJobs(Array.isArray(jobs) ? jobs : [])
      } catch (error) {
        console.error("Failed to fetch assignable jobs:", error)
        toast.error('Failed to fetch assignable jobs')
        setAssignableJobs([])
      } finally {
        setIsLoadingJobs(false)
      }
    },
    [JOBS_PAGE_SIZE]
  )

  // Reset state when dialog opens or user context is missing
  useEffect(() => {
    if (!dialogOpen) {
      return
    }

    setJobSearchTerm('')

    if (!selectedUserId || selectedUserId === "null") {
      setAssignableJobs([])
      setSelectedJobs([])
      setSelectedJobsMap(new Map())
    }
  }, [dialogOpen, selectedUserId])

  useEffect(() => {
    if (!dialogOpen || !selectedUserId || selectedUserId === "null") {
      return
    }

    const searchValue = jobSearchTerm.trim()

    if (!searchValue) {
      // Fetch all assignable jobs - all jobs are shown in the list with checkboxes
      fetchAssignableJobsForUser(selectedUserId)
      return
    }

    const handler = setTimeout(() => {
      fetchAssignableJobsForUser(selectedUserId, { search: searchValue })
    }, 300)

    return () => clearTimeout(handler)
  }, [jobSearchTerm, dialogOpen, selectedUserId, fetchAssignableJobsForUser])

  useEffect(() => {
    if (!isListingTPO) return

    if (dialogOpen && selectedUserId && selectedUserId !== "null") {
      const selectedUser = localUsers.find(user => user.id === selectedUserId)
      if (selectedUser) {
        // For ADMIN: show all assigned jobs
        // For FBDE and FREELANCE_COUNSELLOR: only show jobs assigned by them
        let assignedJobIds: string[] = []
        
        if (rbacUser.user.role === 'ADMIN' || rbacUser.user.role === 'MASTER_BDE') {
          assignedJobIds = Array.isArray(selectedUser.assignedJobs)
            ? selectedUser.assignedJobs.map((job: any) => {
                const jobId = job.jobId?.toString ? job.jobId.toString() : String(job.jobId || job);
                return jobId;
              }).filter((id: string) => id && id !== 'undefined' && id !== 'null')
            : []
        } else if (rbacUser.user.role === 'FBDE' || rbacUser.user.role === 'CONSULTANT' || rbacUser.user.role === 'FREELANCE_COUNSELLOR') {
          // Filter to only include jobs assigned by the current user
          const currentUserId = normalizeUserId(rbacUser.user.id)
          assignedJobIds = Array.isArray(selectedUser.assignedJobs)
            ? selectedUser.assignedJobs
                .filter((job: any) => {
                  if (!job.assignedBy) return false
                  const assignedById = normalizeUserId(job.assignedBy)
                  return assignedById === currentUserId
                })
                .map((job: any) => {
                  const jobId = job.jobId?.toString ? job.jobId.toString() : String(job.jobId || job);
                  return jobId;
                })
                .filter((id: string) => id && id !== 'undefined' && id !== 'null')
            : []
        }
        
        setInitialSelectedJobs(assignedJobIds) // Store initial state
        setSelectedJobs(prev => {
          const prevSorted = [...prev].sort()
          const nextSorted = [...assignedJobIds].sort()
          const isSame =
            prevSorted.length === nextSorted.length &&
            prevSorted.every((id, index) => id === nextSorted[index])
          return isSame ? prev : assignedJobIds
        })
      }
    } else if (!dialogOpen) {
      // Reset initial state when dialog closes
      setInitialSelectedJobs([])
    }
  }, [dialogOpen, selectedUserId, localUsers, isListingTPO, rbacUser.user.role, rbacUser.user.id])

  // Pre-check jobs when they appear in assignableJobs list (including search results)
  useEffect(() => {
    if (!dialogOpen || !isListingTPO || initialSelectedJobs.length === 0) return

    // When assignableJobs changes, check if any of the jobs in the list are already assigned
    // and add them to selectedJobs if they're not already there
    setSelectedJobs(prev => {
      const assignableJobIds = assignableJobs
        .map(job => getJobId(job))
        .filter((id): id is string => Boolean(id))

      // Find jobs that are in both initialSelectedJobs and assignableJobIds but not in prev
      const jobsToAdd = initialSelectedJobs.filter(
        jobId => assignableJobIds.includes(jobId) && !prev.includes(jobId)
      )

      if (jobsToAdd.length > 0) {
        // Also store job details for these pre-selected jobs
        setSelectedJobsMap(prevMap => {
          const newMap = new Map(prevMap)
          jobsToAdd.forEach(jobId => {
            const job = assignableJobs.find(j => getJobId(j) === jobId)
            if (job && !newMap.has(jobId)) {
              newMap.set(jobId, {
                title: job.title || 'Untitled Job',
                companyName: job.companyName || job.company?.name || 'Company',
                location: job.location || 'Location not specified'
              })
            }
          })
          return newMap
        })
        return [...prev, ...jobsToAdd]
      }

      return prev
    })
  }, [assignableJobs, initialSelectedJobs, dialogOpen, isListingTPO])

  // Initialize selectedJobsMap with initial selected jobs when dialog opens
  // Fetch job details for assigned jobs that aren't in the current assignableJobs list
  useEffect(() => {
    if (!dialogOpen || !isListingTPO || initialSelectedJobs.length === 0) return

    const fetchMissingJobDetails = async () => {
      const assignableJobIds = assignableJobs
        .map(job => getJobId(job))
        .filter((id): id is string => Boolean(id))

      // Find jobs that are assigned but not in the current assignableJobs list
      const missingJobIds = initialSelectedJobs.filter(
        jobId => !assignableJobIds.includes(jobId)
      )

      // For jobs already in assignableJobs, add them to the map
      setSelectedJobsMap(prevMap => {
        const newMap = new Map(prevMap)
        initialSelectedJobs.forEach(jobId => {
          if (!newMap.has(jobId)) {
            const job = assignableJobs.find(j => getJobId(j) === jobId)
            if (job) {
              newMap.set(jobId, {
                title: job.title || 'Untitled Job',
                companyName: job.companyName || job.company?.name || 'Company',
                location: job.location || 'Location not specified'
              })
            }
          }
        })
        return newMap
      })

      // Fetch details for missing jobs
      if (missingJobIds.length > 0) {
        try {
          const jobDetailsPromises = missingJobIds.map(async (jobId) => {
            try {
              const response = await getJobDetailsById(jobId)
              const job = response?.data?.job
              if (job) {
                return {
                  jobId,
                  title: job.title || 'Untitled Job',
                  companyName: job.company?.name || 'Company',
                  location: job.location || 'Location not specified'
                }
              }
              return null
            } catch (error) {
              console.error(`Failed to fetch job details for ${jobId}:`, error)
              return null
            }
          })

          const jobDetails = await Promise.all(jobDetailsPromises)
          
          setSelectedJobsMap(prevMap => {
            const newMap = new Map(prevMap)
            jobDetails.forEach(detail => {
              if (detail && !newMap.has(detail.jobId)) {
                newMap.set(detail.jobId, {
                  title: detail.title,
                  companyName: detail.companyName,
                  location: detail.location
                })
              }
            })
            return newMap
          })
        } catch (error) {
          console.error('Error fetching missing job details:', error)
        }
      }
    }

    fetchMissingJobDetails()
  }, [dialogOpen, selectedUserId, initialSelectedJobs, assignableJobs, localUsers, isListingTPO])


  const assignJobTpo = async (userId: string, jobIds: string[], addedJobIds?: string[], removedJobIds?: string[]) => {
    try {
      const response = await assignJobTpoApi(userId, jobIds, addedJobIds, removedJobIds)
      return response
    } catch (error) {

      console.error("Failed to assign job:", error)
    }
  }
  const handlePhoneChange = async () => {
    if (!actionUser || !newPhone.trim()) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setPhoneUpdateLoading(true);
    
    try {
      // Call the API to update user details
      await updateUserDetails(actionUser.id, { phone: newPhone });
      
      // Update the local users list
      const updatedUsers = users.map(u =>
        u.id === actionUser.id ? { ...u, phone: newPhone } : u
      );
      setUsers(updatedUsers);
      
      console.log(`Changed phone number for user ${actionUser.username} to: ${newPhone}`);
      toast.success('Phone number updated successfully');
      
      // Reset form
      setNewPhone('');
      setActionUser(null);
    } catch (error) {
      console.error('Failed to update phone number:', error);
      toast.error('Failed to update phone number');
    } finally {
      setPhoneUpdateLoading(false);
    }
  };

 
    const handleBlockToggle = (user: User) => {
      setBlockConfirmUser(user);
    };

    const confirmBlockToggle = () => {
      if (blockConfirmUser) {
        onBlockToggle(blockConfirmUser);
        setBlockConfirmUser(null);
      }
    };

  useEffect(() => {
    if (!blockConfirmUser) {
      setTimeout(() => {
        document.body.style.removeProperty("pointer-events");
        document.body.removeAttribute("data-scroll-locked");
      }, 0);
    }
  }, [blockConfirmUser]);

  useEffect(() => {
    if (!showEditUserModal) {
      setTimeout(() => {
        document.body.style.removeProperty("pointer-events");
        document.body.removeAttribute("data-scroll-locked");
      }, 0);
    }
  }, [showEditUserModal]);


  const SortableHeader: React.FC<{ column: string; children: React.ReactNode; className?: string }> = ({
    column,
    children,
    className
  }) => (
    <th
      className={cn(
        "px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors bg-[#F5F1F9]",
        className
      )}
      onClick={() => onSort(column)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortBy === column && (
          sortOrder === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        )}
      </div>
    </th>
  );

  const renderActionsDropdown = (user: User) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => onUserSelect(user)}>
          <Eye className="mr-2 h-4 w-4" />
          View Details
        </DropdownMenuItem>
        
        { rbacUser.user.role !== 'FREELANCE_COUNSELLOR' && <DropdownMenuItem onClick={() => onUserReassign(user)}>
          <UserCheck className="mr-2 h-4 w-4" />
          Reassign User
        </DropdownMenuItem>}
        {user.isBlocked ? (
          <DropdownMenuItem 
            onClick={() => handleBlockToggle(user)}
            disabled={blockLoading === user.id || rbacUser.user.role === 'FREELANCE_COUNSELLOR'}
          >
            <Shield className="mr-2 h-4 w-4" />
            {blockLoading === user.id ? 'Unblocking...' : 'Unblock User'}
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem 
            onClick={() => handleBlockToggle(user)}
            disabled={blockLoading === user.id || rbacUser.user.role === 'FREELANCE_COUNSELLOR'}
          >
            <ShieldOff className="mr-2 h-4 w-4" />
            {blockLoading === user.id ? 'Blocking...' : 'Block User'}
          </DropdownMenuItem>
        )}
        {(rbacUser.user.role === 'ADMIN' || rbacUser.user.role === 'SHM' || rbacUser.user.role === 'SHM_ADMIN' || rbacUser.user.role === 'AGENCY_ADMIN' || rbacUser.user.role === 'FBDE_ADMIN') && (
          <DropdownMenuItem
            onClick={() => {
              setEditUser(user);
              setEditUserForm({
                username: String(user.username || ''),
                email: String(user.email || ''),
                phone: String(user.phone || ''),
              });
              setEditUserErrors({ username: '', email: '', phone: '' });
              setShowEditUserModal(true);
            }}
          >
            <Pencil className="mr-2 h-4 w-4" />
            Edit User
          </DropdownMenuItem>
        )}
        {rbacUser.user.role === 'ADMIN' && user.role === 'HM' && (
          <DropdownMenuItem
            disabled={canCreateUsersLoading === user.id}
            onClick={async () => {
              const next = !user.canCreateUsers;
              try {
                setCanCreateUsersLoading(user.id);
                await updateUserCanCreateUsers(user.id, next);
                setUsers(
                  users.map((u) =>
                    u.id === user.id ? { ...u, canCreateUsers: next } : u
                  )
                );
                toast.success(
                  next
                    ? 'Create User enabled for this HM'
                    : 'Create User disabled for this HM'
                );
              } catch {
                // toast handled in API helper
              } finally {
                setCanCreateUsersLoading(null);
              }
            }}
          >
            <UserPlus className="mr-2 h-4 w-4" />
            {canCreateUsersLoading === user.id
              ? 'Updating...'
              : user.canCreateUsers
                ? 'Disable Create User'
                : 'Enable Create User'}
          </DropdownMenuItem>
        )}
        {rbacUser.user.role === 'ADMIN' && (user.role === 'SHM' || user.role === 'HM' || user.role === 'SHM_ADMIN' || user.role === 'FBDE_ADMIN') && (
          <DropdownMenuItem
            disabled={canPostJobsLoading === user.id}
            onClick={async () => {
              const next = !user.canPostJobs;
              try {
                setCanPostJobsLoading(user.id);
                await updateUserCanPostJobs(user.id, next);
                setUsers(
                  users.map((u) =>
                    u.id === user.id ? { ...u, canPostJobs: next } : u
                  )
                );
                toast.success(
                  next
                    ? 'Job Posting enabled for this user'
                    : 'Job Posting disabled for this user'
                );
              } catch {
                // toast handled in API helper
              } finally {
                setCanPostJobsLoading(null);
              }
            }}
          >
            <Briefcase className="mr-2 h-4 w-4" />
            {canPostJobsLoading === user.id
              ? 'Updating...'
              : user.canPostJobs
                ? 'Disable Job Posting'
                : 'Enable Job Posting'}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="bg-background">
      <div className="max-h-[calc(100vh-200px)]" style={{ maxWidth: '100vw' }}>
        <table className="min-w-full divide-y divide-border">
          <thead style={{ backgroundColor: '#FFF5F2' }}>
            <tr>
              <th className="px-4 py-3 w-12 bg-[#FFF5F2]"></th>
              <SortableHeader column="username" className="min-w-48 bg-[#e7c8be] border-r border-border">
                Username
              </SortableHeader>
              <SortableHeader column="email" className="bg-[#FFF5F2] min-w-64">Email</SortableHeader>
              <SortableHeader column="phone" className="bg-[#FFF5F2] min-w-32">Phone</SortableHeader>
              <SortableHeader column="role" className="bg-[#FFF5F2] min-w-32">Role</SortableHeader>
              <SortableHeader column="jobs" className="bg-[#FFF5F2] min-w-32">Jobs</SortableHeader>
              <SortableHeader column="location" className="bg-[#FFF5F2] min-w-48">Location</SortableHeader>
              <SortableHeader column="ledBy" className="bg-[#FFF5F2] min-w-48">Led By </SortableHeader>
              <SortableHeader column="createdAt" className="bg-[#FFF5F2] min-w-40">Created At</SortableHeader>
              <SortableHeader column="lastActivity" className="bg-[#FFF5F2] min-w-40">Last Activity</SortableHeader>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32 bg-[#FFF5F2]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-background divide-y divide-border">
             {isloading ? (
                <>
                  {[...Array(8)].map((_, index) => (
                    <tr key={`shimmer-${index}`} className="bg-white divide-y divide-border">
                      {/* Empty column */}
                      <td className="px-4 py-4"></td>
                      {/* Username */}
                      <td className="px-4 py-4 bg-white border-r border-border">
                        <div className="flex items-center gap-3">
                          <div className="animate-pulse bg-gray-200 rounded w-32 h-4"></div>
                          <div className="animate-pulse bg-gray-200 rounded-full w-16 h-5"></div>
                        </div>
                      </td>
                      {/* Email */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-48 h-4"></div>
                      </td>
                      {/* Phone */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-24 h-4"></div>
                      </td>
                      {/* Role */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-20 h-6"></div>
                      </td>
                      {/* Jobs */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-16 h-6"></div>
                      </td>
                      {/* Location */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-36 h-4"></div>
                      </td>
                      {/* Led By */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-28 h-4"></div>
                      </td>
                      {/* Created At */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-24 h-4"></div>
                      </td>
                      {/* Last Activity */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-24 h-4"></div>
                      </td>
                      {/* Actions */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-8 h-8"></div>
                      </td>
                    </tr>
                  ))}
                </>
              ) : localUsers.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-12 text-center">
                    <p className="text-muted-foreground">No applications found matching your criteria.</p>
                  </td>
                </tr>
              ) : (
                
            localUsers.map((user) => (
              <React.Fragment key={user.id}>
                <tr
                  className={cn(
                    "hover:bg-muted/30 transition-colors cursor-pointer"
                  )}
                  onClick={() => onUserSelect(user)}
                >
                  <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                   
                  </td>
                  <td className="px-4 py-4 bg-white border-r border-border">
                    <div className="relative group">
                      <div className="flex items-center gap-3">
                        {user.profilePhotoUrl && user.profilePhotoUrl.trim() !== '' ? (
                          <img
                            src={user.profilePhotoUrl}
                            alt={user.username || 'Profile'}
                            className="w-8 h-8 rounded-full object-cover flex-shrink-0 border-2 border-gray-200"
                            onError={(e) => {
                              // Fallback to initial letter if image fails to load
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const fallback = target.nextElementSibling as HTMLElement;
                              if (fallback) {
                                fallback.style.display = 'flex';
                                fallback.classList.remove('hidden');
                              }
                            }}
                          />
                        ) : null}
                        <div 
                          className={`w-8 h-8 bg-[#EE7D63] rounded-full flex items-center justify-center flex-shrink-0 ${user.profilePhotoUrl && user.profilePhotoUrl.trim() !== '' ? 'hidden' : ''}`}
                        >
                          <span className="text-white font-medium text-sm">
                            {user?.username?.[0]?.toUpperCase() ?? ''}
                          </span>
                        </div>
                        <span className={`font-medium transition-colors cursor-pointer ${user.isBlocked ? 'text-[#F28B82] hover:text-[#E6492D]' : 'text-foreground hover:text-primary'}`}>
                          {user.username}
                        </span>
                        {user.isBlocked && (
                          <Badge variant="destructive" className="text-xs ml-2">
                            Blocked
                          </Badge>
                        )}
                      </div>
                      <div className="absolute top-0 left-[calc(100%+0.5rem)] mt-1 w-80 p-4 bg-white shadow-lg rounded border border-border opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto z-30">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-lg">{user.username}</h4>
                          <Badge
                            variant={user.isBlocked ? 'default' : 'success'}
                            className="text-xs"
                          >
                            {user.isBlocked ? 'Blocked' : 'Active'}
                          </Badge>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Role:</span>
                            <Badge variant="secondary" className="text-xs">
                              {user.role}
                            </Badge>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Location:</span>
                            <div className="max-w-48 truncate" title={user.location}>
                              {user.location}
                            </div>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Phone:</span>
                            <span className="font-medium">{user.phone}</span>
                          </div>
                        </div>
                        <div className="pt-2 border-t border-border">
                          <div className="text-xs text-muted-foreground">
                            Joined {new Date(user.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-foreground">
                    <span
                      className="cursor-pointer hover:text-orange-600"
                      title="Copy email"
                      onClick={e => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(user.email);
                        toast.success('Email copied!');
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.textDecoration = 'underline';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.textDecoration = 'none';
                      }}
                    >
                      {user.email}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-foreground" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      {/* Email with copy-to-clipboard */}
                          
                      {/* Mobile/Phone with copy-to-clipboard */}
                      <span
                        className="cursor-pointer hover:text-orange-600"
                        title="Copy phone number"
                        onClick={() => {
                          navigator.clipboard.writeText(user.phone);
                          toast.success('Phone number copied!');
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.textDecoration = 'none';
                        }}
                      >
                        {user.phone}
                      </span>
                    
                    </div>
                  </td>
                  <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                    <div className="flex flex-wrap items-center gap-1">
                    <Dialog onOpenChange={setIsRoleDialogOpen}>
                      <DialogTrigger asChild>
                        <Badge
                          className={getRoleBadgeColor(user.role)}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActionUser(user);
                          }}
                        >
                          {user.role === "FREELANCE_COUNSELLOR" ? "Freelance Counsellor" : user.role === "MASTER_BDE" ? "Master BDE" : user.role === "FREELANCE_BDE" ? "Free BDE" : user.role === "BDE_INTERN" ? "BDE Intern" : user.role}
                        </Badge>
                      </DialogTrigger>
                      <DialogContent className="bg-white shadow-lg rounded-lg border border-gray-200 max-w-md px-[16px] py-[25px] gap-2">
                        <DialogHeader>
                          <DialogTitle className="font-semibold text-lg mb-4 text-gray-800">
                            Role History of <span className="font-bold text-[#EC6E51]">{actionUser?.username || user.username}</span>
                          </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-6 py-[16px]" style={{ maxHeight: '400px', overflowY: 'scroll', }}>
                          {actionUser?.roleHistory && actionUser.roleHistory.length > 0 ? (
                            [...actionUser.roleHistory]
                              .sort((a, b) => {
                                if (a.endDate === null) return -1;
                                if (b.endDate === null) return 1;
                                return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
                              })
                              .map((history, index) => (
                                <div
                                  key={index}
                                  className="flex items-start border-b border-gray-100 pb-4 last:border-b-0"
                                  style={{ position: 'relative' }}
                                >
                                  <div className="flex items-center justify-center w-6 h-6 mr-4" style={{ position: 'relative' }}>
                                    <motion.div
                                      className={`w-4 h-4 z-10 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors duration-300 ${index < progress
                                        ? "bg-[#ED795F] border-[#ED795F] text-white"
                                        : "bg-white border-gray-300"
                                        }`}
                                      initial={{ scale: 1, opacity: 1 }}
                                      animate={{
                                        scale: index < progress ? [1, 1.2, 1] : 1,
                                        opacity: index < progress ? [1, 0.8, 1] : 1,
                                      }}
                                      transition={{
                                        duration: 0.8,
                                        repeat: index < progress && history.endDate === null ? Infinity : 0,
                                        ease: "easeInOut",
                                      }}
                                    >
                                      {index < progress && history.endDate !== null && (
                                        <svg
                                          className="w-3 h-3"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="3"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        >
                                          <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                      )}
                                    </motion.div>
                                    {history.endDate === null && index < progress && (
                                      <>
                                        <motion.div
                                          className="absolute left-0 rounded-full border-2 border-[#ED795F] bg-transparent z-0"
                                          style={{
                                            width: "75%",
                                            height: "75%",
                                            top: "13%",
                                            left: "10.2%",
                                            transform: "translate(-50%, -50%)",
                                          }}
                                          animate={{
                                            scale: [1, 1.8, 2.4],
                                            opacity: [0.7, 0.4, 0],
                                          }}
                                          transition={{
                                            duration: 1,
                                            repeat: Infinity,
                                            ease: "easeOut",
                                          }}
                                        />
                                        <motion.div
                                          className="absolute rounded-full border-2 border-[#ED795F] bg-transparent z-0"
                                          style={{
                                            width: "75%",
                                            height: "75%",
                                            top: "13%",
                                            left: "10.2%",
                                            transform: "translate(-50%, -50%)",
                                          }}
                                          animate={{
                                            scale: [1, 1.8, 2.4],
                                            opacity: [0.7, 0.4, 0],
                                          }}
                                          transition={{
                                            duration: 1,
                                            repeat: Infinity,
                                            ease: "easeOut",
                                            delay: 0.3,
                                          }}
                                        />
                                      </>
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between mb-1">
                                      <span
                                        className={`font-semibold text-sm ${index < progress ? "text-gray-800" : "text-gray-400"}`}
                                      >
                                        {history.role}
                                      </span>
                                    </div>
                                    <div className="text-xs space-y-1">
                                      <div className="flex justify-between">
                                        <span className="text-gray-500">Start Date:</span>
                                        <span
                                          className={`${index < progress ? "text-gray-600" : "text-gray-400"} font-medium`}
                                        >
                                          {new Date(history.startDate).toLocaleDateString()}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-500">End Date:</span>
                                        <span
                                          className={`${index < progress ? "text-gray-600" : "text-gray-400"} font-medium`}
                                          style={{ margin: history.endDate ? "" : "10px 0px" }}
                                        >
                                          {history.endDate ? (
                                            new Date(history.endDate).toLocaleDateString()
                                          ) : (
                                            <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded">
                                              Current
                                            </span>
                                          )}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-500">Manager:</span>
                                        <span
                                          className={`${index < progress ? "text-gray-600" : "text-gray-400"} font-medium`}
                                        >
                                          {history.manager?.username || 'N/A'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-500">Manager Email:</span>
                                        <span
                                          className={`${index < progress ? "text-gray-600" : "text-gray-400"} font-medium`}
                                        >
                                          {history.manager?.email || 'N/A'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))
                          ) : (
                            <p className="text-sm text-gray-500">No role history available for {actionUser?.username || user.username}.</p>
                          )}
                        </div>
                      </DialogContent>
                    </Dialog>
                    {user.role === 'HM' && user.canCreateUsers && (
                      <Badge variant="outline" className="text-xs border-orange-300 text-orange-700 bg-orange-50">
                        Can create users
                      </Badge>
                    )}
                    {(user.role === 'SHM' || user.role === 'HM' || user.role === 'SHM_ADMIN' || user.role === 'FBDE_ADMIN') && user.canPostJobs && (
                      <Badge variant="outline" className="text-xs border-blue-300 text-blue-700 bg-blue-50">
                        Post Jobs
                      </Badge>
                    )}
                    </div>
                  </td>
                  <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex items-center">
                      <div
                        className="rounded-full text-black text-xs bg-accent font-bold flex items-center justify-center transition-colors duration-200 border border-input hover:text-accent-foreground cursor-pointer hover:bg-orange-100"
                        style={{ width: 36, height: 36 }}
                        onClick={async () => {
                          if ((user.jobCount ?? 0) > 0) {
                            setAssignedJobsUserId(user.id)
                            setAssignedJobsDialogOpen(true)
                            setIsLoadingAssignedJobs(true)
                            try {
                              const response = await getJobsAssignedToUser(user.id)
                              if (response?.status === 'success' && response?.data?.jobs) {
                                setAssignedJobs(response.data.jobs)
                              } else {
                                setAssignedJobs([])
                                toast.error('Failed to load assigned jobs')
                              }
                            } catch (error) {
                              console.error('Failed to fetch assigned jobs:', error)
                              setAssignedJobs([])
                            } finally {
                              setIsLoadingAssignedJobs(false)
                            }
                          }
                        }}
                        title="Click to view assigned jobs"
                      >
                        {user.assignedJobs?.length ?? 0}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs font-medium rounded-full h-auto bg-accent p-[10px] min-w-[16px] min-h-[16px] -ml-2.5 hover:bg-orange-600 text-orange-500 hover:text-white"
                        onClick={() => {
                          setSelectedUserId(user.id)
                          
                          // For ADMIN: show all assigned jobs
                          // For FBDE and FREELANCE_COUNSELLOR: only show jobs assigned by them
                          let assignedJobIds: string[] = []
                          
                          if (rbacUser.user.role === 'ADMIN') {
                            assignedJobIds = Array.isArray(user.assignedJobs)
                              ? user.assignedJobs.map((job: any) => {
                                  const jobId = job.jobId?.toString ? job.jobId.toString() : String(job.jobId || job);
                                  return jobId;
                                }).filter((id: string) => id && id !== 'undefined' && id !== 'null')
                              : []
                          } else if (rbacUser.user.role === 'FBDE' || rbacUser.user.role === 'CONSULTANT' || rbacUser.user.role === 'FREELANCE_COUNSELLOR') {
                            // Filter to only include jobs assigned by the current user
                            const currentUserId = normalizeUserId(rbacUser.user.id)
                            assignedJobIds = Array.isArray(user.assignedJobs)
                              ? user.assignedJobs
                                  .filter((job: any) => {
                                    if (!job.assignedBy) return false
                                    const assignedById = normalizeUserId(job.assignedBy)
                                    return assignedById === currentUserId
                                  })
                                  .map((job: any) => {
                                    const jobId = job.jobId?.toString ? job.jobId.toString() : String(job.jobId || job);
                                    return jobId;
                                  })
                                  .filter((id: string) => id && id !== 'undefined' && id !== 'null')
                              : []
                          }
                          
                          setSelectedJobs(assignedJobIds)
                          setDialogOpen(true)
                        }}
                      >
                        <UserPlus className="w-[13px] cursor-pointer" />
                      </Button>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-foreground">
                    <div className="max-w-48 truncate" title={user.location}>
                      {user.location}
                      
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-foreground">{user.currentManager?.username || 'N/A'}</td>
                  <td className="px-4 py-4 text-sm text-muted-foreground">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td
                    className="px-4 py-4 text-sm text-muted-foreground"
                    title={user.lastActivity ? new Date(user.lastActivity).toLocaleString() : undefined}
                  >
                    {formatRelativeTime(user.lastActivity)}
                  </td>
                  <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                    {renderActionsDropdown(user)}
                  </td>
                </tr>
               
              </React.Fragment>
              )
            ))}
          </tbody>
        </table>
      </div>
      {/* {users.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No users found matching your criteria.</p>
        </div>
      )} */}

      {/* Block/Unblock Confirmation Dialog */}
      <AlertDialog open={!!blockConfirmUser} onOpenChange={() => setBlockConfirmUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {blockConfirmUser?.isBlocked ? 'Unblock User' : 'Block User'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to {blockConfirmUser?.isBlocked ? 'unblock' : 'block'} the user{' '}
              <strong>{blockConfirmUser?.username}</strong>?
              {blockConfirmUser?.isBlocked 
                ? ' This will restore their access to the system.' 
                : ' This will prevent them from accessing the system.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBlockToggle}
              className={blockConfirmUser?.isBlocked ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {blockConfirmUser?.isBlocked  ? 'Unblock User' : 'Block User'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showEditUserModal} onOpenChange={setShowEditUserModal}>
        <DialogContent className="w-[95vw] max-w-md rounded-xl bg-white">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update username, email and phone.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="space-y-1">
              <Label htmlFor="edit-user-username">Username *</Label>
              <Input
                id="edit-user-username"
                value={editUserForm.username}
                onChange={(e) => {
                  setEditUserForm((p) => ({ ...p, username: e.target.value }));
                  setEditUserErrors((p) => ({ ...p, username: '' }));
                }}
              />
              {editUserErrors.username && <p className="text-xs text-red-600">{editUserErrors.username}</p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-user-email">Email *</Label>
              <Input
                id="edit-user-email"
                type="email"
                value={editUserForm.email}
                onChange={(e) => {
                  setEditUserForm((p) => ({ ...p, email: e.target.value }));
                  setEditUserErrors((p) => ({ ...p, email: '' }));
                }}
              />
              {editUserErrors.email && <p className="text-xs text-red-600">{editUserErrors.email}</p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-user-phone">Phone *</Label>
              <Input
                id="edit-user-phone"
                value={editUserForm.phone}
                inputMode="numeric"
                maxLength={10}
                onChange={(e) => {
                  setEditUserForm((p) => ({
                    ...p,
                    phone: e.target.value.replace(/\D/g, '').slice(0, 10),
                  }));
                  setEditUserErrors((p) => ({ ...p, phone: '' }));
                }}
              />
              {editUserErrors.phone && <p className="text-xs text-red-600">{editUserErrors.phone}</p>}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="outline"
              onClick={() => {
                setShowEditUserModal(false);
                setEditUser(null);
                setEditUserErrors({ username: '', email: '', phone: '' });
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={editUserSaving}
              onClick={async () => {
                if (!editUser?.id) return;
                const payload = {
                  username: editUserForm.username.trim(),
                  email: editUserForm.email.trim(),
                  phone: editUserForm.phone.trim(),
                };
                const nextErrors = {
                  username: payload.username ? '' : 'Username is required.',
                  email: payload.email ? '' : 'Email is required.',
                  phone: payload.phone ? '' : 'Phone is required.',
                };
                setEditUserErrors(nextErrors);
                if (nextErrors.username || nextErrors.email || nextErrors.phone) return;
                setEditUserSaving(true);
                try {
                  await updateUserContactDetails(editUser.id, payload);
                  const updated = localUsers.map((u) =>
                    u.id === editUser.id
                      ? { ...u, username: payload.username, email: payload.email, phone: payload.phone }
                      : u
                  );
                  setLocalUsers(updated);
                  setUsers(updated);
                  setShowEditUserModal(false);
                  setEditUser(null);
                  toast.success('User updated successfully.');
                } catch (error: any) {
                  toast.error(error?.response?.data?.message || 'Failed to update user.');
                } finally {
                  setEditUserSaving(false);
                }
              }}
            >
              {editUserSaving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-full max-w-[95vw] sm:max-w-3xl p-0">
          <div className="bg-white rounded-3xl shadow-2xl w-full flex flex-col max-h-[90vh] overflow-hidden sm:max-h-[80vh]">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-gray-900">
                  Assign Jobs to User
                </DialogTitle>
                <DialogDescription className="text-sm text-gray-500">
                  Select jobs to assign to this user
                </DialogDescription>
              </DialogHeader>
            </div>

            <div className="px-4 sm:px-6 space-y-4 transition-all duration-300 overflow-y-auto">
              {/* Jobs Search and Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Available Jobs</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by job title, company, or location..."
                    value={jobSearchTerm}
                    onChange={(e) => setJobSearchTerm(e.target.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => setIsSearchFocused(false)}
                    className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all duration-200"
                    ref={inputRef}
                  />
                </div>
              </div>

              <div className="border border-gray-200 rounded-2xl h-[45vh] sm:h-80 overflow-y-auto bg-white">
                {isLoadingJobs ? (
                  <div className="space-y-0">
                    {[1, 2, 3, 4, 5].map((index) => (
                      <div
                        key={index}
                        className="flex items-center p-3 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="w-10 h-10 bg-gray-200 rounded animate-pulse mr-4"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: `${60 + index * 5}%` }}></div>
                          <div className="h-3 bg-gray-200 rounded animate-pulse" style={{ width: `${40 + index * 3}%` }}></div>
                        </div>
                        <div className="w-5 h-5 bg-gray-200 rounded-md animate-pulse"></div>
                      </div>
                    ))}
                  </div>
                ) : assignableJobs.length > 0 ? (
                  <>
                    <div
                      onClick={handleToggleSelectAllFiltered}
                      className="flex items-center justify-between p-3 bg-orange-50/60 hover:bg-orange-50 cursor-pointer transition-colors duration-150 border-b border-gray-100"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-orange-300 to-orange-500 rounded flex items-center justify-center text-white font-semibold">
                          <Briefcase className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">Select All</p>
                          <p className="text-xs text-gray-500">
                            {areAllFilteredSelected ? 'Clear selection' : 'Select all jobs in this list'}
                          </p>
                        </div>
                      </div>
                      <div
                        className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200 ${
                          areAllFilteredSelected
                            ? "bg-orange-500 border-orange-500"
                            : "border-gray-300 bg-gray-50 group-hover:border-orange-400"
                        }`}
                      >
                        {areAllFilteredSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                    </div>
                    {assignableJobs.map((job: any) => {
                      const jobId = getJobId(job)
                      const isSelected = selectedJobs.includes(jobId)
                      return (
                        <div
                          key={jobId || job.title}
                          onClick={() => handleJobToggle(jobId)}
                          className="flex items-center p-3 hover:bg-orange-50 cursor-pointer transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-600 rounded flex items-center justify-center text-white font-medium text-lg mr-4">
                            {job.title?.charAt(0).toUpperCase() || 'J'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{job.title || 'Untitled Job'}</p>
                            <p className="text-xs text-gray-500 truncate">
                              {job.companyName || job.company?.name || 'Company'} • {job.location || 'Location not specified'}
                            </p>
                          </div>
                          <div
                            className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200 ${
                              isSelected
                                ? "bg-orange-500 border-orange-500"
                                : "border-gray-300 bg-gray-50 group-hover:border-orange-400"
                            }`}
                          >
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                        </div>
                      )
                    })}
                  </>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-gray-500">
                    <Briefcase className="w-8 h-8 mb-2 opacity-50" />
                    <p className="text-sm">No jobs found</p>
                  </div>
                )}
              </div>

              {selectedJobs.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-orange-600">
                    {selectedJobs.length} job{selectedJobs.length !== 1 ? "s" : ""} selected
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {getSelectedJobTitles().map((job) => {
                      return (
                        <div
                          key={job.id}
                          className="flex items-center bg-orange-100 text-orange-700 text-xs font-medium pl-2.5 pr-1 py-1 rounded-full"
                        >
                          <span>{job.title}</span>
                          <button
                            onClick={() => handleRemoveJob(job.id)}
                            className="ml-1.5 p-0.5 rounded-full hover:bg-orange-200 focus:outline-none"
                            aria-label={`Remove ${job.title}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 p-4 sm:p-6 border-t border-gray-100 bg-gray-50 rounded-b-3xl">
              <button
                type="button"
                onClick={() => setDialogOpen(false)}
                className="w-full sm:flex-1 px-4 py-3 text-gray-800 font-semibold bg-white rounded-xl border border-gray-300 hover:bg-gray-100 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  setAssignmentInProgress(true);
                  if (!selectedUserId || selectedUserId === "null") {
                    toast.error('Please select a user')
                    return
                  }
               
                  const jobsToAssign = Array.from(new Set(selectedJobs.filter((id: string) => id && id !== 'undefined' && id !== 'null')))
                  
                  // Calculate added and removed jobs
                  const addedJobIds = jobsToAssign.filter((id: string) => !initialSelectedJobs.includes(id))
                  const removedJobIds = initialSelectedJobs.filter((id: string) => !jobsToAssign.includes(id))
                  
                  console.log("Initial jobs:", initialSelectedJobs)
                  console.log("Final jobs:", jobsToAssign)
                  console.log("Added jobs:", addedJobIds)
                  console.log("Removed jobs:", removedJobIds)

                  try { 
                    const response = await assignJobTpo(selectedUserId, jobsToAssign, addedJobIds, removedJobIds)
                    if (response?.status === "success") {
                      const updatedUsers = users.map((existingUser) => {
                        if (existingUser.id !== selectedUserId) {
                          return existingUser
                        }
                        // Format assignedJobs as array of objects with jobId, assignedBy, assignedAt
                        const formattedAssignedJobs = jobsToAssign.map((jobId: string) => ({
                          jobId: jobId,
                          assignedBy: rbacUser.user.id,
                          assignedAt: new Date().toISOString(),
                        }))
                        return {
                          ...existingUser,
                          assignedJobs: formattedAssignedJobs,
                        }
                      })
                      setUsers(updatedUsers as UserWithAssignments[])
                      setLocalUsers(updatedUsers as UserWithAssignments[])
                      toast.success(`Successfully updated job assignments`)
                      setDialogOpen(false)
                      setSelectedJobs([])
                      setSelectedJobsMap(new Map())
                      setJobSearchTerm('')
                    } else {
                      toast.error("Failed to assign jobs.")
                    }
                  } catch (error) {
                   
                    toast.error("Failed to assign jobs")
                  } finally {
                    setAssignmentInProgress(false);
                  }
                }}
                disabled={!selectedUserId || selectedUserId === "null" || isLoadingJobs}
                className="w-full sm:flex-1 px-4 py-3 bg-orange-500 text-white font-semibold rounded-xl hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-orange-500/20 hover:shadow-xl hover:shadow-orange-500/30 disabled:shadow-none"
              >

                  {assignmentInProgress ? "assigning..."  : "Update Assignments"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assigned Jobs List Dialog */}
      <Dialog open={assignedJobsDialogOpen} onOpenChange={setAssignedJobsDialogOpen}>
        <DialogContent className="w-full max-w-[95vw] sm:max-w-3xl p-0">
          <div className="bg-white rounded-3xl shadow-2xl w-full flex flex-col max-h-[90vh] overflow-hidden sm:max-h-[80vh]">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-gray-900">
                  Assigned Jobs
                </DialogTitle>
                <DialogDescription className="text-sm text-gray-500">
                  {assignedJobsUserId && users.find(u => u.id === assignedJobsUserId)?.username && 
                    `Jobs assigned to ${users.find(u => u.id === assignedJobsUserId)?.username}`}
                </DialogDescription>
              </DialogHeader>
            </div>

            <div className="px-4 sm:px-6 py-4 overflow-y-auto flex-1">
              {isLoadingAssignedJobs ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((index) => (
                    <div
                      key={index}
                      className="flex items-center p-4 border border-gray-200 rounded-xl"
                    >
                      <div className="w-12 h-12 bg-gray-200 rounded-lg animate-pulse mr-4"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: `${60 + index * 5}%` }}></div>
                        <div className="h-3 bg-gray-200 rounded animate-pulse" style={{ width: `${40 + index * 3}%` }}></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : assignedJobs.length > 0 ? (
                <div className="space-y-3">
                  {assignedJobs.map((job: any) => (
                    <div
                      key={job.id}
                      className="flex items-start p-4 border border-gray-200 rounded-xl hover:border-orange-300 hover:bg-orange-50/30 transition-all duration-200"
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-lg flex items-center justify-center text-white font-semibold text-lg mr-4 flex-shrink-0">
                        {job.title?.charAt(0).toUpperCase() || 'J'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-gray-900 mb-1">
                          {job.title || 'Untitled Job'}
                        </h3>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p className="flex items-center gap-2">
                            <span className="font-medium">Company:</span>
                            <span>{job.companyName || 'N/A'}</span>
                          </p>
                          <p className="flex items-center gap-2">
                            <span className="font-medium">Location:</span>
                            <span>{job.location || 'N/A'}</span>
                          </p>
                          {job.assignedBy && (
                            <p className="flex items-center gap-2">
                              <span className="font-medium">Assigned by:</span>
                              <span>{job.assignedBy.username || job.assignedBy.email || 'N/A'}</span>
                            </p>
                          )}
                          {job.assignedAt && (
                            <p className="flex items-center gap-2">
                              <span className="font-medium">Assigned at:</span>
                              <span>{new Date(job.assignedAt).toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}</span>
                            </p>
                          )}
                          {job.assignmentLevel && (
                            <p className="flex items-center gap-2">
                              <span className="font-medium">Level:</span>
                              <Badge variant="outline" className="text-xs">
                                {job.assignmentLevel}
                              </Badge>
                            </p>
                          )}
                          {job.status && (
                            <p className="flex items-center gap-2">
                              <span className="font-medium">Status:</span>
                              <Badge 
                                variant={job.status === 'PUBLISHED' ? 'default' : 'secondary'}
                                className="text-xs"
                              >
                                {job.status}
                              </Badge>
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-gray-500 py-12">
                  <Briefcase className="w-12 h-12 mb-4 opacity-50" />
                  <p className="text-base font-medium">No jobs assigned</p>
                  <p className="text-sm text-gray-400 mt-1">This user has no active job assignments</p>
                </div>
              )}
            </div>

            <div className="flex justify-end p-4 sm:p-6 border-t border-gray-100 bg-gray-50 rounded-b-3xl">
              <button
                type="button"
                onClick={() => {
                  setAssignedJobsDialogOpen(false)
                  setAssignedJobs([])
                  setAssignedJobsUserId(null)
                }}
                className="px-6 py-2 text-gray-800 font-semibold bg-white rounded-xl border border-gray-300 hover:bg-gray-100 transition-all duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserTable;